// Make changes — the ⚙ panel for one field, written to the transaction's CORE
// model (Customize's TxnFieldPanel is the overlay one). Four tabs in plain
// words: Basics (name, what people enter, choices, picked from, help, starts
// with, section, width, show / change / list / freeze), Rules (must fill,
// unique, identifies the record, capitals, length, decimals, smallest /
// largest, shown as), When it changes (sentences → ItemChangeRule) and Checks
// (sentences → ValidationRule).
//
// Like Customize's panel, changes are HELD and saved together by Done — one
// panel = one save = one undo step; Esc or a click outside drops them. Delete
// is the exception: it asks first (how many records hold a value, Hide it
// instead), then saves at once.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ColumnDef, EditMaskValue, SqlModel, Validation } from '../txn/types'
import { isHidden, isListHidden, listOrderedColumns } from '../txn/utils'
import { groupColumns } from '../txn/FormRenderer'
import type { CustomizeDiff, CustomizeSession } from './session'
import type { BuildOps, BuildSources } from './build/types'
import { colPath, columnKey, formBase, formPath, groupKey, truthy } from './txnOps'
import {
  AnchoredPanel, ChipGroup, PanelHeader, PanelNote, PanelRow, PanelSelect, PanelTabs, PanelText,
  RuleLine, Switch, VIOLET, VIOLET_LINE, VioletButton,
} from './ui'
import {
  DECIMAL_CHOICES, FIELD_KINDS, NEXT_NUMBER, STARTS_WITH, WIDTH_CHOICES,
  decimalsOf, kindLeaves, kindOf, lookupSpec, numberSize, parseLookup,
} from './build/fieldKinds'
import { defaultNextNumber, isNumbering, nextNumberFromModel, nextNumberPreview, nextNumberToModel, type NextNumber } from './build/series'
import { CALC_OPS, icText, readItemChange, writeItemChange, type CalcOp, type IcSentence } from './build/itemChange'
import {
  COMPARE_OPS, LIMITS_SUFFIX, UNIQUE_SUFFIX, checkText, checkToRule, defaultCheckMessage, findRule,
  isRule, limitsOf, limitsRule, ruleToCheck, uniqueRule, uniqueRuleName,
  type CheckSentence, type CompareOp, type FieldCheck,
} from './build/checks'
import { validationSummary } from '../txn/types'
import { tw } from '../testHooks'
import { Loader2, Trash2 } from 'lucide-react'

type Tab = 'basics' | 'rules' | 'change' | 'checks'
/** A change held until Done: a value, or the leaf removed. */
type Pending = { reset: true } | { reset?: false; value: unknown }

export interface BuildFieldPanelProps {
  session: CustomizeSession
  objName: string
  form: SqlModel
  formNo: string
  /** The header form (the list's) — its fields are offered to checks too. */
  isHeader: boolean
  colKey: string
  gridLabel: string
  anchor: HTMLElement | null
  onClose: () => void
  transport: BuildOps
  /** Open at a tab (a freshly added field opens on Rules). */
  initialTab?: Tab
}

const textKinds = new Set(['words', 'paragraph', 'email', 'phone'])
const numberKinds = new Set(['number', 'money'])
const dateKinds = new Set(['date', 'datetime'])

export default function BuildFieldPanel({
  session, objName, form, formNo, isHeader, colKey, gridLabel, anchor, onClose, transport, initialTab,
}: BuildFieldPanelProps) {
  const col = useMemo(
    () => (form.columns || []).map((w) => w.column).find((c) => c && columnKey(c) === colKey),
    [form, colKey],
  ) as ColumnDef | undefined
  const [tab, setTab] = useState<Tab>(initialTab || 'basics')
  const [pending, setPendingState] = useState<Record<string, Pending>>({})
  const pendingRef = useRef(pending)
  const labelsRef = useRef<string[]>([])
  const [applying, setApplying] = useState(false)
  const [refused, setRefused] = useState(false)
  const [warn, setWarn] = useState<string | null>(null)
  const [usage, setUsage] = useState<{ count: number; records: number } | null>(null)
  const [sources, setSources] = useState<BuildSources | null>(null)
  const [askDelete, setAskDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    transport.fieldUsage(objName, formNo, colKey)
      .then((u) => { if (!cancelled) setUsage(u) })
      .catch(() => { if (!cancelled) setUsage(null) })
    transport.sources()
      .then((s) => { if (!cancelled) setSources(s) })
      .catch(() => { if (!cancelled) setSources({ screens: [], reports: [] }) })
    return () => { cancelled = true }
  }, [transport, objName, formNo, colKey])

  if (!col) return null
  const label0 = String(col.heading || col.name || colKey)
  if (col.uda) {
    return (
      <AnchoredPanel anchor={anchor} onClose={onClose} label={label0} width={420}>
        <PanelHeader title={label0} sub="Added by an organization" saved={false} />
        <div style={{ padding: '10px 16px 14px' }}>
          <PanelNote>An organization added this field for itself — it is set up under Additional attributes.</PanelNote>
        </div>
      </AnchoredPanel>
    )
  }

  const p = (leaf: string) => colPath(formNo, colKey, leaf)
  const self = String(col.db_name || col.name || colKey)

  // The field as it is, with the held changes on top.
  const shown = { ...col } as ColumnDef & Record<string, unknown>
  for (const [path, held] of Object.entries(pending)) {
    if (!path.startsWith(p(''))) continue
    shown[path.slice(p('').length)] = held.reset ? undefined : held.value
  }
  const label = String(shown.heading || col.name || colKey)

  const stage = (changes: Record<string, unknown>, what: string) => {
    const next = { ...pendingRef.current }
    for (const [leaf, value] of Object.entries(changes)) {
      const path = leaf.startsWith('transaction.') ? leaf : p(leaf)
      next[path] = value === null || value === undefined ? { reset: true } : { value }
    }
    pendingRef.current = next
    labelsRef.current = [...labelsRef.current, `${what} · ${label}`]
    setPendingState(next)
    setRefused(false)
  }

  const done = async () => {
    const held = pendingRef.current
    const diff: CustomizeDiff = { set: [], reset: [] }
    for (const [path, h] of Object.entries(held)) {
      if (h.reset) diff.reset.push(path)
      else diff.set.push({ path, value: h.value })
    }
    if (!diff.set.length && !diff.reset.length) { onClose(); return }
    const labels = labelsRef.current
    setApplying(true)
    try {
      const ok = await session.apply(diff, labels.length === 1 ? labels[0] : `Changed ${label}`)
      if (ok) onClose()
      else setRefused(true)
    } finally {
      setApplying(false)
    }
  }

  // ── what this field is ────────────────────────────────────────────────
  const kind = kindOf(shown)
  const locked = (usage?.count || 0) > 0
  const key = truthy(shown.key)
  const mandatory = truthy(shown.mandatory)
  const hasDefault = String(shown.default_value ?? '').trim() !== ''
  const calculated = !!String(col.sql_expression ?? '').trim()
  const cols = (form.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[]
  const labelOf = (n: string) => {
    const c = cols.find((x) => [x.db_name, x.name, x.col_id].some((k) => String(k || '').toLowerCase() === n.toLowerCase()))
    return String(c?.heading || c?.name || n)
  }
  const others = cols.filter((c) => columnKey(c) !== colKey && !c.uda && !String(c.sql_expression ?? '').trim())
  const otherOptions = others.map((c) => ({ value: String(c.db_name || c.name), label: String(c.heading || c.name) }))

  const guardMandatory = (next: { hide?: boolean; lock?: boolean; clearsDefault?: boolean }) => {
    if (!mandatory || key) return true
    const hidden = next.hide ?? isHidden(shown)
    const lockd = next.lock ?? truthy(shown.protect)
    const defaulted = next.clearsDefault ? false : hasDefault
    if ((hidden || lockd) && !defaulted) {
      setWarn(`${label} must be filled in. Give it a starting value first — otherwise nobody can fill it in.`)
      return false
    }
    setWarn(null)
    return true
  }

  // Groups (sections) of this form, in display order.
  const groups = groupColumns(cols, false, [], form.group_settings).map(([g]) => g)
  const groupLabel = (g: string) =>
    String(form.group_settings?.[groupKey(g)]?.label ?? '').trim() || (g === 'Others' ? 'Other information' : g)

  // The list / grid: where this column sits, for "freeze up to here".
  const listCols = listOrderedColumns(form)
  const listIdx = listCols.findIndex((c) => columnKey(c) === colKey)
  const frozenPath = formPath(formNo, 'frozen_columns')
  const heldFrozen = pending[frozenPath]
  const frozen = Number(heldFrozen ? (heldFrozen.reset ? 0 : heldFrozen.value) : form.frozen_columns) || 0

  // ── picked from ───────────────────────────────────────────────────────
  const lookup = parseLookup(String(shown.lookup ?? ''))
  const screens = sources?.screens || []
  const pickedScreen = lookup ? screens.find((s) => s.table.toLowerCase() === lookup.table.toLowerCase()) : undefined
  const pickFrom = (objNameOf: string) => {
    const scr = screens.find((s) => s.obj_name === objNameOf)
    if (!scr) return
    const data = scr.key_column || scr.columns[0]?.db_name || ''
    const display = scr.columns.find((c) => !c.key && /name|title|descr/i.test(c.db_name))?.db_name
      || scr.columns.find((c) => !c.key)?.db_name || data
    stage({ lookup: lookupSpec({ table: scr.table, display, data }), lookup_add_object: scr.obj_name }, 'Picked from')
  }

  // ── validations (Rules + Checks share the column's list) ──────────────
  const validations: Validation[] = (shown.validations as Validation[] | undefined) || []
  const setValidations = (next: Validation[], what: string) => stage({ validations: next }, what)
  const unique = !!findRule(validations, `${self}${UNIQUE_SUFFIX}`)
  const limits = limitsOf(validations, self)
  const withRule = (name: string, rule: Validation | null) => {
    const rest = validations.filter((v) => !(isRule(v) && v.name === name))
    return rule ? [...rest, rule] : rest
  }
  const fieldChecks = validations.map((v) => ({ v, c: ruleToCheck(self, v) }))
  const unreadableChecks = fieldChecks.filter((x) => !x.c && !(isRule(x.v)
    && (x.v.name.endsWith(UNIQUE_SUFFIX) || x.v.name.endsWith(LIMITS_SUFFIX))))

  // ── item change ───────────────────────────────────────────────────────
  const ic = readItemChange(shown.item_change as never, self)
  const writeIc = (sentences: IcSentence[], what: string) =>
    stage({ item_change: writeItemChange({ self, sentences, previous: shown.item_change as never, lookup }) }, what)

  const ruleCount = [mandatory, unique, key, shown.text_case === 'upper', !!(limits.min || limits.max),
    !!Number(shown.min_length || 0)].filter(Boolean).length
  const checkCount = fieldChecks.filter((x) => x.c).length + unreadableChecks.length
  const changes = Object.keys(pending).length

  // ── delete ────────────────────────────────────────────────────────────
  const deleteField = async (drop: boolean) => {
    setDeleting(true)
    try {
      const ok = drop
        ? await session.apply({
          set: [], reset: [],
          ops: [{ op: 'remove', array: `${formBase(formNo)}.columns`, key: colKey, drop_data: true }],
        }, `Deleted ${label}`)
        : await session.apply({ set: [{ path: p('hidden'), value: true }], reset: [] }, `Hid ${label}`)
      if (ok) onClose()
      else setRefused(true)
    } finally {
      setDeleting(false)
      setAskDelete(false)
    }
  }

  const summary = [kind.label, mandatory ? 'must be filled in' : '', key ? 'identifies the record' : '']
    .filter(Boolean).join(' · ')

  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label={label} width={480}>
      <PanelHeader title={label} sub={summary} saved={false} />
      <PanelTabs<Tab> value={tab} onChange={setTab} tabs={[
        { id: 'basics', label: 'Basics' },
        { id: 'rules', label: 'Rules', count: ruleCount },
        { id: 'change', label: 'When it changes', count: ic.sentences.length + ic.unreadable.length },
        { id: 'checks', label: 'Checks', count: checkCount },
      ]} />
      <div style={{ padding: '12px 16px 6px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {calculated && <PanelNote>Worked out by the database — it can be renamed, moved, hidden or frozen here; the rest is in the full designer.</PanelNote>}

        {tab === 'basics' && (
          <>
            <PanelText label="Name on the form" value={String(shown.heading ?? '')}
              onCommit={(v) => { if (v.trim()) stage({ heading: v.trim() }, 'Renamed') }} />
            <PanelText label="Heading in the list" value={String(shown.list_heading ?? '')}
              placeholder="Same as the name" onCommit={(v) => stage({ list_heading: v.trim() || null }, 'List heading')} />
            {!calculated && (
              <PanelSelect label="What people enter" value={kind.id} disabled={locked || key}
                options={FIELD_KINDS.map((k) => ({ value: k.id, label: k.label }))}
                hint={locked ? `Can’t be changed: ${usage!.count} record${usage!.count === 1 ? '' : 's'} hold a value. Add a new field instead.`
                  : key ? 'Can’t be changed: it identifies the record.' : undefined}
                onChange={(id) => {
                  const leaves = kindLeaves(id, {
                    values: (shown.edit_mask as { values?: EditMaskValue[] } | undefined)?.values,
                    lookup: id === 'pick' ? lookup : null,
                  })
                  if (id !== 'pick') leaves.lookup_add_object = null
                  stage(leaves, 'What people enter')
                }} />
            )}
            {kind.id === 'choice' && (
              <ChoicesEditor values={(shown.edit_mask as { values?: EditMaskValue[] } | undefined)?.values || []}
                onChange={(values) => stage({ edit_mask: { ...(shown.edit_mask || {}), mask: (shown.edit_mask as { mask?: string } | undefined)?.mask || 'ddlb', values } }, 'Choices')} />
            )}
            {kind.id === 'pick' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                <PanelSelect label="Picked from" value={pickedScreen?.obj_name || ''} disabled={locked}
                  options={[{ value: '', label: sources ? 'Choose a screen…' : 'Loading…' },
                    ...screens.filter((s) => s.obj_name.toLowerCase() !== objName.toLowerCase() || !isHeader)
                      .map((s) => ({ value: s.obj_name, label: s.title }))]}
                  onChange={pickFrom} />
                <PanelSelect label="People see" value={lookup?.display || ''} disabled={!pickedScreen}
                  options={(pickedScreen?.columns || []).map((c) => ({ value: c.db_name, label: c.heading }))}
                  onChange={(display) => lookup && stage({ lookup: lookupSpec({ ...lookup, display }) }, 'People see')} />
              </div>
            )}
            <PanelText label="Help text" value={String(shown.descr ?? '')} placeholder="A short hint shown under the field"
              onCommit={(v) => stage({ descr: v.trim() }, 'Help text')} />
            {!calculated && !key && (
              <StartsWith shown={shown} kindId={kind.id}
                onChange={(leaves) => {
                  if (!guardMandatory({ clearsDefault: !leaves.default_value })) return
                  stage(leaves, 'Starts with')
                }} />
            )}
            {isHeader && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Section</span>
                  <ChipGroup label="Section" hook="make-changes-section" value={groupKey(shown.group_name)}
                    options={groups.map((g) => ({ value: groupKey(g), label: groupLabel(g) }))}
                    onChange={(g) => {
                      const raw = cols.find((c) => groupKey(c.group_name) === g)?.group_name
                      stage({ group_name: raw ?? (g === 'Others' ? '' : g) }, 'Section')
                    }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Width on the form</span>
                  <ChipGroup label="Width on the form" hook="make-changes-width"
                    value={nearestWidth(Number(shown.width) || 200)}
                    options={WIDTH_CHOICES.map((w) => ({ value: w.id, label: w.label }))}
                    onChange={(id) => stage({ width: WIDTH_CHOICES.find((w) => w.id === id)?.width ?? 200 }, 'Width')} />
                </div>
              </>
            )}
            <PanelRow title="Show on the form" hint="Hidden fields wait in the tray under the form">
              <Switch label="Show on the form" on={!isHidden(shown)} onChange={(on) => {
                if (!on && !guardMandatory({ hide: true })) return
                stage({ hidden: !on }, on ? 'Show' : 'Hide')
              }} />
            </PanelRow>
            <PanelRow title="Can be changed" hint={key ? 'It identifies the record' : 'Off makes it read-only for everyone'} dim={calculated}>
              <Switch label="Can be changed" disabled={calculated} on={!truthy(shown.protect)} onChange={(on) => {
                if (!on && !guardMandatory({ lock: true })) return
                stage({ protect: !on }, on ? 'Can be changed' : 'Read-only')
              }} />
            </PanelRow>
            <PanelRow title={`Show in ${gridLabel}`} hint="A column of the list">
              <Switch label={`Show in ${gridLabel}`} on={!isListHidden(shown)}
                onChange={(on) => stage({ list_hidden: !on }, on ? 'Show in the list' : 'Hide from the list')} />
            </PanelRow>
            <PanelRow title="Freeze up to here" hint={listIdx < 0 ? 'Show it in the list first' : 'It and every column before it stay in view'} dim={listIdx < 0}>
              <Switch label="Freeze up to here" disabled={listIdx < 0} on={listIdx >= 0 && frozen === listIdx + 1}
                onChange={(on) => stage({ [frozenPath]: on ? listIdx + 1 : 0 }, on ? 'Freeze' : 'Unfreeze')} />
            </PanelRow>
          </>
        )}

        {tab === 'rules' && (
          <>
            <PanelRow title="Must be filled in" hint="The record can’t be saved without it">
              <Switch label="Must be filled in" disabled={key} on={mandatory || key} onChange={(on) => {
                if (on && (isHidden(shown) || truthy(shown.protect)) && !hasDefault) {
                  setWarn(`${label} is hidden or read-only. Give it a starting value first, or show it and let people change it.`)
                  return
                }
                setWarn(null)
                stage({ mandatory: on }, on ? 'Must be filled in' : 'Optional')
              }} />
            </PanelRow>
            <PanelRow title="No two records the same" hint="Nobody can save a value another record already has">
              <Switch label="No two records the same" on={unique}
                onChange={(on) => setValidations(withRule(`${self}${UNIQUE_SUFFIX}`, on ? uniqueRule(self, label) : null), on ? 'No two the same' : 'Duplicates allowed')} />
            </PanelRow>
            <PanelRow title="Identifies the record" hint={locked || (usage?.records || 0) > 0 ? 'Can’t be changed once there are records' : 'Like a claim number — one per record'}
              dim={(usage?.records || 0) > 0}>
              <Switch label="Identifies the record" disabled={(usage?.records || 0) > 0 || calculated} on={key}
                onChange={(on) => stage(on ? { key: true, mandatory: true } : { key: false }, on ? 'Identifies the record' : 'Does not identify the record')} />
            </PanelRow>
            {textKinds.has(kind.id) && (
              <>
                <PanelRow title="Capital letters only" hint="Typed letters become capitals, like a code">
                  <Switch label="Capital letters only" on={shown.text_case === 'upper'}
                    onChange={(on) => stage({ text_case: on ? 'upper' : null }, on ? 'Capital letters' : 'Any letters')} />
                </PanelRow>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  <PanelText label="Shortest (letters)" value={String(shown.min_length ?? '')} placeholder="Any"
                    onCommit={(v) => stage({ min_length: v.trim() ? Math.max(0, Math.round(Number(v) || 0)) : null }, 'Shortest')} />
                  <PanelText label="Longest (letters)" value={sizeOf(shown.db_size)} placeholder="No limit"
                    disabled={locked}
                    onCommit={(v) => { const n = Math.round(Number(v)); if (n > 0) stage({ db_size: String(n) }, 'Longest') }} />
                </div>
              </>
            )}
            {numberKinds.has(kind.id) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Decimal places</span>
                <ChipGroup label="Decimal places" hook="make-changes-decimals" disabled={locked}
                  value={decimalsOf(shown)} options={DECIMAL_CHOICES.map((d) => ({ value: d, label: String(d) }))}
                  onChange={(d) => stage({
                    db_size: kind.id === 'money' ? `14,${d}` : numberSize(d),
                    ...(kind.id === 'money' ? { display: { ...(shown.display || {}), type: 'number', decimals: d } } : {}),
                  }, 'Decimal places')} />
              </div>
            )}
            {(numberKinds.has(kind.id) || dateKinds.has(kind.id)) && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                <PanelText label={dateKinds.has(kind.id) ? 'Earliest' : 'Smallest'} value={limits.min}
                  placeholder={dateKinds.has(kind.id) ? 'e.g. 2026-01-01' : 'e.g. 0'}
                  onCommit={(v) => setValidations(withRule(`${self}${LIMITS_SUFFIX}`, limitsRule(self, label, v, limits.max)), 'Limits')} />
                <PanelText label={dateKinds.has(kind.id) ? 'Latest' : 'Largest'} value={limits.max}
                  placeholder={dateKinds.has(kind.id) ? 'e.g. 2026-12-31' : 'e.g. 100000'}
                  onCommit={(v) => setValidations(withRule(`${self}${LIMITS_SUFFIX}`, limitsRule(self, label, limits.min, v)), 'Limits')} />
              </div>
            )}
            {numberKinds.has(kind.id) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Shown as</span>
                <ChipGroup label="Shown as" hook="make-changes-format"
                  value={numberFormatOf(shown.display as { type?: string; numberStyle?: string } | undefined)}
                  options={[{ value: 'plain', label: 'Plain' }, { value: 'grouped', label: '1,234.50' }, { value: 'percent', label: 'Percent' }]}
                  onChange={(f) => stage({
                    display: f === 'plain' ? null
                      : { type: 'number', numberStyle: f === 'percent' ? 'percent' : 'decimal', decimals: decimalsOf(shown) },
                  }, 'Shown as')} />
              </div>
            )}
          </>
        )}

        {tab === 'change' && (
          <ChangeTab ic={ic} self={self} label={label} labelOf={labelOf} otherOptions={otherOptions}
            canFill={!!lookup} pickedTitle={pickedScreen?.title || lookup?.table || 'the picked record'}
            sourceOptions={(pickedScreen?.columns || []).map((c) => ({ value: c.db_name, label: c.heading }))}
            onChange={writeIc} />
        )}

        {tab === 'checks' && (
          <ChecksTab self={self} label={label} kindId={kind.id} labelOf={labelOf} otherOptions={otherOptions}
            checks={fieldChecks.filter((x) => x.c).map((x) => x.c!)}
            unreadable={unreadableChecks.map((x) => typeof x.v === 'string' ? x.v : validationSummary(x.v))}
            onAdd={(c) => setValidations([...validations, checkToRule(self, c)], 'Check added')}
            onRemove={(name) => setValidations(validations.filter((v) => !(isRule(v) && v.name === name)), 'Check removed')}
            newName={(t) => uniqueRuleName(`${self}_${t}`, validations)} />
        )}

        {warn && <PanelNote tone="warn">{warn} Not changed.</PanelNote>}
      </div>
      {refused && (
        <div style={{ padding: '0 16px' }}>
          <PanelNote tone="warn">Not saved. {session.errors.join(' · ') || 'The change was refused.'}</PanelNote>
        </div>
      )}
      {askDelete && (
        <div role="alert" style={{ margin: '0 16px 8px', padding: '10px 12px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 14 }}>
            {usage && usage.count > 0
              ? `${usage.count} record${usage.count === 1 ? '' : 's'} hold a value in ${label}. Deleting the field deletes those values too.`
              : `Delete ${label}? No record holds a value in it yet.`}
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <VioletButton quiet onClick={() => { void deleteField(false) }} disabled={deleting || (mandatory && !hasDefault)} hook="make-changes-hide-instead">Hide it instead</VioletButton>
            <button type="button" onClick={() => { void deleteField(true) }} disabled={deleting} {...tw('action', 'make-changes-delete-field')}
              style={{ height: 32, padding: '0 14px', borderRadius: 6, border: 0, background: '#b42318', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
              {deleting && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />} Delete it and its values
            </button>
            <button type="button" onClick={() => setAskDelete(false)}
              style={{ height: 32, padding: '0 10px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 16px 14px', borderTop: '1px solid var(--color-border, #f1f3f6)', marginTop: 6 }}>
        <button type="button" disabled={key} onClick={() => setAskDelete(true)} {...tw('action', 'make-changes-ask-delete')}
          title={key ? 'It identifies the record, so it can’t be deleted' : undefined}
          style={{ border: 0, background: 'transparent', color: key ? '#9ca3af' : '#b42318', cursor: key ? 'default' : 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }}>
          <Trash2 size={14} />Delete this field
        </button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
            {changes ? 'Esc or a click outside drops them' : 'Esc or click outside to close'}
          </span>
          <VioletButton onClick={() => { void done() }} disabled={applying} hook="make-changes-panel-done">
            {applying && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}Done
          </VioletButton>
        </span>
      </div>
    </AnchoredPanel>
  )
}

function sizeOf(v: unknown): string {
  const m = String(v ?? '').match(/^\s*(\d+)\s*$/)
  return m ? m[1] : ''
}

function nearestWidth(w: number): string {
  let best = WIDTH_CHOICES[0]
  for (const c of WIDTH_CHOICES) if (Math.abs(c.width - w) < Math.abs(best.width - w)) best = c
  return best.id
}

function numberFormatOf(d: { type?: string; numberStyle?: string } | undefined): string {
  if (!d || d.type !== 'number') return 'plain'
  return d.numberStyle === 'percent' ? 'percent' : 'grouped'
}

// ── Basics › The choices ─────────────────────────────────────────────────

function ChoicesEditor({ values, onChange }: { values: EditMaskValue[]; onChange: (v: EditMaskValue[]) => void }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const t = draft.trim()
    if (!t || values.some((v) => v.display.toLowerCase() === t.toLowerCase())) return
    onChange([...values, { data: t, display: t }])
    setDraft('')
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>The choices</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {values.length === 0 && <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary, #6b7280)' }}>No choices yet.</span>}
        {values.map((v) => (
          <span key={v.data} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 30, padding: '0 4px 0 10px', borderRadius: 15, background: '#ede9fe', color: '#4c1d95', fontWeight: 500, fontSize: 14 }}>
            {v.display}
            <button type="button" aria-label={`Remove ${v.display}`} onClick={() => onChange(values.filter((x) => x.data !== v.data))}
              style={{ border: 0, background: 'transparent', color: '#4c1d95', cursor: 'pointer', fontSize: 15 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={draft} placeholder="Add a choice" onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          {...tw('field', 'make-changes-choice')}
          style={{ flex: 1, height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit', fontSize: 14 }} />
        <VioletButton quiet onClick={add} hook="make-changes-add-choice">Add</VioletButton>
      </div>
    </div>
  )
}

// ── Basics › Starts with ─────────────────────────────────────────────────

function StartsWith({ shown, kindId, onChange }: {
  shown: ColumnDef & Record<string, unknown>
  kindId: string
  onChange: (leaves: Record<string, unknown>) => void
}) {
  const dv = String(shown.default_value ?? '')
  const numeric = kindId === 'number' || kindId === 'money'
  const nn = nextNumberFromModel({ default_value: dv, series: shown.series as never })
  const options = STARTS_WITH.filter((o) => !o.kinds || o.kinds.includes(kindId))
  const current = isNumbering(dv) ? 'next'
    : options.find((o) => o.value !== undefined && (o.value === dv || o.datetime_value === dv) && o.id !== 'nothing')?.id
      ?? (dv ? '' : 'nothing')
  const [n, setN] = useState<NextNumber>(nn ?? defaultNextNumber())
  const writeNext = (next: NextNumber) => {
    setN(next)
    const m = nextNumberToModel(next, { numeric })
    onChange({ default_value: m.default_value, series: m.series })
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>Starts with</span>
      <ChipGroup label="Starts with" hook="make-changes-starts" value={current}
        options={options.map((o) => ({ value: o.id, label: o.label }))}
        onChange={(id) => {
          if (id === 'next') { writeNext(nn ?? n); return }
          const o = options.find((x) => x.id === id)
          const v = kindId === 'datetime' && o?.datetime_value ? o.datetime_value : (o?.value ?? '')
          onChange({ default_value: v, series: null })
        }} />
      {current !== 'next' && (
        <PanelText label="Or a value" value={current === '' ? dv : ''} placeholder="e.g. INR, Draft"
          onCommit={(v) => onChange({ default_value: v.trim(), series: null })} />
      )}
      {current === 'next' && !numeric && (
        nn === null && isNumbering(dv) && !/^\s*auto_generate(_prefixed)?\(\)\s*$/i.test(dv)
          ? <PanelNote>Numbered the way the full designer set it up.</PanelNote>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 8, background: '#f5f3ff' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>How the number looks</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                <PanelText label="Starts with the letters" value={n.letters} placeholder="e.g. EC"
                  onCommit={(v) => writeNext({ ...n, letters: v.trim() })} />
                <PanelText label="Digits" value={String(n.digits)}
                  onCommit={(v) => writeNext({ ...n, digits: Math.max(1, Math.min(12, Math.round(Number(v) || NEXT_NUMBER.digits))) })} />
              </div>
              <ChipGroup label="The year" hook="make-changes-year" value={n.year}
                options={NEXT_NUMBER.year_choices.map((y) => ({ value: y.id, label: y.label }))}
                onChange={(year) => writeNext({ ...n, year })} />
              <span style={{ fontSize: 14 }}>Next one: <strong {...tw('field', 'make-changes-series-preview')}>{nextNumberPreview(n)}</strong></span>
            </div>
          )
      )}
      {current === 'next' && numeric && <span style={{ fontSize: 13.5 }}>Counts 1, 2, 3…</span>}
    </div>
  )
}

// ── When it changes ──────────────────────────────────────────────────────

function ChangeTab({ ic, self, label, labelOf, otherOptions, canFill, pickedTitle, sourceOptions, onChange }: {
  ic: ReturnType<typeof readItemChange>
  self: string
  label: string
  labelOf: (n: string) => string
  otherOptions: { value: string; label: string }[]
  canFill: boolean
  pickedTitle: string
  sourceOptions: { value: string; label: string }[]
  onChange: (sentences: IcSentence[], what: string) => void
}) {
  type T = IcSentence['type']
  const types: { value: T; label: string }[] = [
    ...(canFill ? [{ value: 'fill' as T, label: `Fill in from ${pickedTitle}` }] : []),
    { value: 'calc', label: 'Work out a field' },
    { value: 'set', label: 'Set a field to a value' },
    { value: 'clear', label: 'Clear a field' },
    { value: 'lock', label: 'Lock a field when…' },
  ]
  const [type, setType] = useState<T>(types[0].value)
  const [target, setTarget] = useState('')
  const [source, setSource] = useState('')
  const [op, setOp] = useState<CalcOp>('*')
  const [other, setOther] = useState('')
  const [number, setNumber] = useState('')
  const [value, setValue] = useState('')
  const t = types.some((x) => x.value === type) ? type : types[0].value
  const draft: IcSentence | null = !target ? null
    : t === 'fill' ? (source ? { type: 'fill', target, source } : null)
    : t === 'calc' ? (other || number.trim() ? { type: 'calc', target, op, ...(other ? { field: other } : { number: number.trim() }) } : null)
    : t === 'set' ? (value.trim() ? { type: 'set', target, value: value.trim() } : null)
    : t === 'clear' ? { type: 'clear', target }
    : (value.trim() ? { type: 'lock', target, value: value.trim() } : null)
  const sourceLabel = (n: string) => sourceOptions.find((o) => o.value === n)?.label || n
  const text = (s: IcSentence) => icText(s, self, labelOf, sourceLabel, pickedTitle)
  if (ic.other) {
    return <PanelNote>When {label} changes, it {ic.other.charAt(0).toLowerCase() + ic.other.slice(1)}. That is set up in the full designer.</PanelNote>
  }
  return (
    <>
      <span style={{ fontSize: 14 }}>When someone changes <strong>{label}</strong>, also:</span>
      {ic.sentences.length + ic.unreadable.length === 0 && <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary, #6b7280)' }}>Nothing yet.</span>}
      {ic.sentences.map((s, i) => (
        <RuleLine key={`s${i}`} icon="⚡" text={text(s)}
          onRemove={() => onChange(ic.sentences.filter((_, j) => j !== i), `Removed: ${text(s)}`)} />
      ))}
      {ic.unreadable.map((u, i) => <RuleLine key={`u${i}`} icon="⚡" text={u} sub="Set up in the full designer" />)}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 8, border: `1px dashed ${VIOLET_LINE}` }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Add what should happen</span>
        <ChipGroup label="What should happen" hook="make-changes-ic-type" value={t} options={types} onChange={(v) => setType(v)} />
        <span style={{ fontSize: 13.5 }}>{t === 'lock' ? 'Lock' : t === 'clear' ? 'Clear' : t === 'fill' ? 'Fill in' : 'Set'}</span>
        <ChipGroup label="Which field" hook="make-changes-ic-target" value={target} options={otherOptions} onChange={setTarget} />
        {t === 'fill' && (
          <>
            <span style={{ fontSize: 13.5 }}>with this from {pickedTitle}:</span>
            <ChipGroup label="From the picked record" hook="make-changes-ic-source" value={source} options={sourceOptions} onChange={setSource} />
          </>
        )}
        {t === 'calc' && (
          <>
            <span style={{ fontSize: 13.5 }}>to {label}</span>
            <ChipGroup label="How" hook="make-changes-ic-op" value={op} options={CALC_OPS.map((o) => ({ value: o.op, label: o.label }))} onChange={setOp} />
            <ChipGroup label="And" hook="make-changes-ic-other" value={other} options={otherOptions}
              onChange={(v) => { setOther(v); setNumber('') }} />
            <input value={number} placeholder="or a number" onChange={(e) => { setNumber(e.target.value); setOther('') }}
              style={{ width: 140, height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
          </>
        )}
        {(t === 'set' || t === 'lock') && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13.5 }}>{t === 'set' ? 'to this value' : `when ${label} is`}</span>
            <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={t === 'set' ? 'e.g. Pending' : 'e.g. Approved'}
              {...tw('field', 'make-changes-ic-value')}
              style={{ height: 32, borderRadius: 6, border: '1px solid var(--color-border, #d5dae1)', padding: '0 10px', fontFamily: 'inherit' }} />
          </label>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13.5, color: draft ? VIOLET : 'var(--color-text-secondary, #6b7280)' }}>
            {draft ? `${text(draft)}.` : 'Fill in the blanks above.'}
          </span>
          <VioletButton disabled={!draft} hook="make-changes-add-ic" onClick={() => {
            if (!draft) return
            onChange([...ic.sentences, draft], `When ${label} changes: ${text(draft).toLowerCase()}`)
            setTarget(''); setSource(''); setOther(''); setNumber(''); setValue('')
          }}>Add</VioletButton>
        </div>
      </div>
    </>
  )
}

// ── Checks ───────────────────────────────────────────────────────────────

function ChecksTab({ self, label, kindId, labelOf, otherOptions, checks, unreadable, onAdd, onRemove, newName }: {
  self: string
  label: string
  kindId: string
  labelOf: (n: string) => string
  otherOptions: { value: string; label: string }[]
  checks: FieldCheck[]
  unreadable: string[]
  onAdd: (c: FieldCheck) => void
  onRemove: (name: string) => void
  newName: (type: string) => string
}) {
  type T = CheckSentence['type']
  const isNum = numberKinds.has(kindId)
  const isDate = dateKinds.has(kindId)
  const isText = textKinds.has(kindId)
  const types: { value: T; label: string }[] = [
    ...((isNum || isDate) ? [{ value: 'range' as T, label: 'Must be between' }] : []),
    ...(isDate ? [{ value: 'future' as T, label: 'Can’t be in the future' }] : []),
    { value: 'reqwhen', label: 'Must be filled in when…' },
    ...((isNum || isDate) ? [{ value: 'compare' as T, label: 'Compared with another field' }] : []),
    ...(isText ? [{ value: 'len' as T, label: 'At least some letters' }] : []),
  ]
  const [type, setType] = useState<T>(types[0].value)
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [field, setField] = useState('')
  const [value, setValue] = useState('')
  const [op, setOp] = useState<CompareOp>('>')
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<'E' | 'W'>('E')
  const t = types.some((x) => x.value === type) ? type : types[0].value
  const draft: CheckSentence | null =
    t === 'range' ? (a.trim() && b.trim() ? { type: 'range', a: a.trim(), b: b.trim() } : null)
    : t === 'future' ? { type: 'future' }
    : t === 'reqwhen' ? (field && value.trim() ? { type: 'reqwhen', field, value: value.trim() } : null)
    : t === 'compare' ? (field ? { type: 'compare', op, field } : null)
    : (Number(a) > 0 ? { type: 'len', n: Math.round(Number(a)) } : null)
  const fallbackMsg = draft ? defaultCheckMessage(label, draft, labelOf) : ''
  return (
    <>
      <span style={{ fontSize: 14 }}>Before saving, check that <strong>{label}</strong>:</span>
      {checks.length + unreadable.length === 0 && <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary, #6b7280)' }}>No checks yet.</span>}
      {checks.map((c) => (
        <RuleLine key={c.name} icon="✓" text={checkText(label, c.sentence, labelOf) + (c.severity === 'W' ? ' — a warning only' : '')}
          sub={`Shows: “${c.message}”`} onRemove={() => onRemove(c.name)} />
      ))}
      {unreadable.map((u, i) => <RuleLine key={`u${i}`} icon="✓" text={u} sub="Set up in the full designer" />)}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 8, border: `1px dashed ${VIOLET_LINE}` }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Add a check</span>
        <ChipGroup label="Kind of check" hook="make-changes-check-type" value={t} options={types} onChange={(v) => { setType(v); setMessage('') }} />
        {(t === 'range' || t === 'len') && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <DraftInput label={t === 'len' ? 'At least how many letters' : 'From'} value={a} onChange={setA}
              placeholder={t === 'len' ? 'e.g. 3' : isDate ? 'e.g. 2026-01-01' : 'e.g. 1'} />
            {t === 'range' && <DraftInput label="To" value={b} onChange={setB} placeholder={isDate ? 'e.g. 2026-12-31' : 'e.g. 100000'} />}
          </div>
        )}
        {(t === 'reqwhen' || t === 'compare') && (
          <>
            {t === 'compare' && (
              <ChipGroup label="How" hook="make-changes-check-op" value={op} options={COMPARE_OPS.map((o) => ({ value: o.op, label: o.label }))} onChange={setOp} />
            )}
            <span style={{ fontSize: 13.5 }}>{t === 'compare' ? 'than this field' : 'when this field'}</span>
            <ChipGroup label="Other field" hook="make-changes-check-field" value={field} options={otherOptions} onChange={setField} />
            {t === 'reqwhen' && (
              <DraftInput label="is" value={value} onChange={setValue} placeholder="e.g. Travel" />
            )}
          </>
        )}
        <DraftInput label="Message people see" value={message} placeholder={fallbackMsg || 'Written for you once the check is complete'} onChange={setMessage} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>If it fails</span>
          <ChipGroup label="If it fails" hook="make-changes-check-severity" value={severity}
            options={[{ value: 'E', label: 'Stop saving' }, { value: 'W', label: 'Warn, then let them save' }]} onChange={setSeverity} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13.5, color: draft ? VIOLET : 'var(--color-text-secondary, #6b7280)' }}>
            {draft ? `Check that ${checkText(label, draft, labelOf)}.` : 'Fill in the blanks above.'}
          </span>
          <VioletButton disabled={!draft} hook="make-changes-add-check" onClick={() => {
            if (!draft) return
            onAdd({ sentence: draft, message: message.trim() || fallbackMsg, severity, name: newName(draft.type) })
            setA(''); setB(''); setField(''); setValue(''); setMessage('')
          }}>Add check</VioletButton>
        </div>
      </div>
    </>
  )
}

/** A live text box for a sentence being built (no blur commit: the Add
 *  button reads it straight away). */
function DraftInput({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        {...tw('field', `make-changes-draft:${label}`)}
        style={{
          height: 34, boxSizing: 'border-box', width: '100%', borderRadius: 6, padding: '0 10px',
          border: '1px solid var(--color-border, #d5dae1)', fontSize: 14, fontFamily: 'inherit',
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
        }} />
    </label>
  )
}
